import { Quote, Person, CheckoutResult } from '../../api/types'
import { Loading } from '../../components/Loading'
import React, { useState, useCallback, useEffect } from 'react'
import styled from 'styled-components'
import { CheckoutTable } from './CheckoutTable'
import { breakpointUp, breakpointDown, breakpoints } from '../../helpers/breakpoints'
import { colors } from '../../constants'
import { Section, SectionDivider, Heading } from './components'
import { ContactInfo, useContactInfoProps, isEmail } from './ContactInfo'
import { PaymentSchedule, usePaymentScheduleProps } from './PaymentSchedule'
import { PaymentMethod, usePaymentMethodProps } from './PaymentType'
import { CreditCard, useCreditCardProps } from './CreditCard'
import { api } from '../../api/api.tm'
import Button from '../../components/Button'
import { propBetween } from '../../helpers/between'
import { UseExistingPaymentMethod } from './UseExistingPaymentMethod'
import { useRouteTransition } from '../../hooks/useRouteTransition'
import { setCancellations, useAppState } from '../../Store'
import { useExceptionHandler } from '../../hooks/useExceptionHandler'
import { useTitle } from '../../hooks/useTitle'
import { useDispatchErrorNotification } from '../../hooks/useDispatchErrorNotification'
import { LogOutButton } from '../../components/LogOutButton'
import { useThreeDSecurePayment } from '../../hooks/useThreeDSecurePayment'
import { PaymentMethod as PaymentMethodType } from '../../api/types'
import {
  addThreeDSBreadcrumb,
  captureThreeDSConfirmPaymentFailure,
  captureThreeDSPaymentConfirmed,
} from '../../components/CreditCard/Cardinal/threeDSecureTelemetry'

// checkout (confirmPayment) result codes that are a card/issuer outcome rather than a technical failure
const DECLINED_CONFIRM_CODES = ['cardNotAuthorized', 'invalidCreditCardNumber']

interface Props {
  quote: Quote
  person: Person
}

const CheckoutComponent = ({ quote, person }: Props) => {
  const { dispatch } = useAppState()
  const contactInfoProps = useContactInfoProps()
  const { routeTo } = useRouteTransition()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errorText, setErrorText] = useState<string | undefined>(undefined)
  const { handleException } = useExceptionHandler()
  const { setTitle } = useTitle()
  const dispatchErrorNotification = useDispatchErrorNotification()
  const paymentMethodProps = usePaymentMethodProps(() => {
    setErrorText(undefined)
  })
  const paymentScheduleProps = usePaymentScheduleProps()
  const creditCardProps = useCreditCardProps(() => {
    setErrorText(undefined)
  })

  // 3DS Payment Hook
  // Sentry logging happens in useThreeDSecurePayment (sanitized and tagged with the failing step)
  const onThreeDSError = useCallback((_error?: any, messageType: 'try again' | 'different method' = 'try again') => {
    const message =
      messageType === 'try again'
        ? 'Eitthvað fór úrskeiðis. Vinsamlegast reynið aftur síðar'
        : 'Eitthvað fór úrskeiðis. Vinsamlegast hafðu samband við kortaútgefandann þinn eða banka. Þú getur valið að greiða með netbanka til að klára kaupin.'
    setErrorText(message)
    setIsLoading(false)
  }, [])

  const updatedPerson = { ...person, email: { personal: contactInfoProps.email } }
  const { isLoadingPayment, initializePayment, recurringPaymentData } = useThreeDSecurePayment(
    updatedPerson,
    onThreeDSError
  )

  useEffect(() => {
    setTitle('Kaupa')
  })

  const handleSuccessfulPayment = useCallback(
    (result: CheckoutResult) => {
      if (!result.success) {
        if (result.messages[0]) {
          if (result.messages[0].code === 'cardNotAuthorized') {
            setErrorText('Því miður virðist ekki vera næg innistæða á kortinu.')
          } else if (result.messages[0].code === 'cardServiceConnectionFailure') {
            dispatchErrorNotification('Því miður næst ekki samband við kortaþjónustu.')
          } else if (result.messages[0].code === 'invalidCreditCardNumber') {
            setErrorText('Kortanúmer er ekki gilt.')
          } else {
            dispatchErrorNotification('Það lítur út fyrir að það séu einhver tæknileg vandræði, prófaðu aftur seinna.')
          }
          setIsLoading(false)
        } else {
          setIsLoading(false)
          throw new Error(result.messages[0])
        }
      } else {
        setIsLoading(false)

        if (result.cancellations.length > 0) {
          dispatch(setCancellations(result.cancellations))
          routeTo(`/${quote.number}/uppsogn`)
        } else {
          routeTo(`/${quote.number}/takk-fyrir`)
        }
      }
    },
    [dispatch, dispatchErrorNotification, quote.number, routeTo]
  )

  const handleCheckout = useCallback(async () => {
    let method: PaymentMethodType
    // Sentry: only the checkout call itself counts as a card payment failure, not what happens after it responded
    let isCardPayment = false
    let hasCheckoutResponded = false
    try {
      if (recurringPaymentData) {
        const expiryDate = recurringPaymentData.recurringCardPayment.tokenExpiryDate
        const expiryMonth = parseInt(expiryDate.substring(5, 7), 10)
        const expiryYear = parseInt(expiryDate.substring(2, 4), 10)
        method = {
          type: 'creditCard' as const,
          schedule: paymentScheduleProps.schedule,
          cardNumber: recurringPaymentData.recurringCardPayment.tokenId,
          cvc: creditCardProps.cardInfo.cvc,
          validTo: { month: expiryMonth, year: expiryYear },
        }
      } else if (person.isCustomer) {
        method = { type: 'useExisting' }
      } else {
        method = {
          type: 'invoice' as const,
          schedule: paymentScheduleProps.schedule,
        }
      }
      isCardPayment = method.type === 'creditCard'
      if (isCardPayment) {
        addThreeDSBreadcrumb('confirm_payment', 'Confirming card payment with policyhub')
      }
      const result = await api.checkout({
        quoteId: quote.number,
        email: contactInfoProps.email,
        paperLess: contactInfoProps.paperLess,
        wantsMarketing: contactInfoProps.wantsMarketing,
        method: method,
      })
      hasCheckoutResponded = true
      if (isCardPayment) {
        if (result.success) {
          captureThreeDSPaymentConfirmed()
        } else {
          const code = result.messages[0]?.code
          captureThreeDSConfirmPaymentFailure(
            code ?? 'checkout returned success: false without a message code',
            code && DECLINED_CONFIRM_CODES.includes(code) ? 'declined' : 'error'
          )
        }
      }
      handleSuccessfulPayment(result)
    } catch (error: any) {
      if (isCardPayment && !hasCheckoutResponded) {
        captureThreeDSConfirmPaymentFailure(error, 'error')
      }
      handleException({
        error,
        dispatchErrorNotification: false,
      })
      setIsLoading(false)
    }
  }, [
    contactInfoProps.email,
    contactInfoProps.paperLess,
    contactInfoProps.wantsMarketing,
    creditCardProps.cardInfo.cvc,
    handleException,
    handleSuccessfulPayment,
    paymentScheduleProps.schedule,
    person.isCustomer,
    quote.number,
    recurringPaymentData,
  ])

  // Monitor 3DS payment completion and proceed with checkout
  useEffect(() => {
    if (recurringPaymentData) {
      // 3DS payment completed successfully, now we can complete the purchase
      handleCheckout()
    }
  }, [recurringPaymentData, person.isCustomer, quote.number, routeTo, handleCheckout])

  const completePurchase = useCallback(async () => {
    try {
      setIsLoading(true)

      // If paying with card and not an existing customer, use 3DS flow
      if (paymentMethodProps.method === 'card' && !person.isCustomer) {
        if (recurringPaymentData?.recurringCardPayment) {
          // If this recurringCardPayment is present then it's likely that policy hub has failed
          handleCheckout()
          return
        }
        // Initialize 3DS payment flow
        await initializePayment({
          cardNumber: creditCardProps.cardInfo.cardNumber.replace(/\s/g, ''),
          cardCVC: creditCardProps.cardInfo.cvc,
          cardExpiry: creditCardProps.cardInfo.expiryDate,
        })
        // The rest of the flow is handled by the useThreeDSecurePayment hook
        // and the useEffect above will complete the checkout when payment is ready
        return
      }

      // Original checkout flow for non-card or existing customer payments
      handleCheckout()
    } catch (error: any) {
      handleException({
        error,
        dispatchErrorNotification: false,
      })
      setIsLoading(false)
    }
  }, [
    paymentMethodProps.method,
    person.isCustomer,
    handleCheckout,
    recurringPaymentData?.recurringCardPayment,
    initializePayment,
    creditCardProps.cardInfo.cardNumber,
    creditCardProps.cardInfo.cvc,
    creditCardProps.cardInfo.expiryDate,
    handleException,
  ])

  // Combined loading state for both regular loading and 3DS payment loading
  const loading = isLoading || isLoadingPayment
  return (
    <Root>
      <PageHeading>Greiðslusíða</PageHeading>

      <CheckoutGrid>
        <CheckOutInfo>
          <Section label="Netfang">
            <ContactInfo {...contactInfoProps} />
          </Section>
          <SectionDivider />
          {person.isCustomer ? (
            <>
              <Section label="">
                <UseExistingPaymentMethod multiplePolicies={quote.policies.length > 1} />
              </Section>
            </>
          ) : (
            <>
              <Section label="Greiðslutíðni">
                <PaymentSchedule {...paymentScheduleProps} />
              </Section>
              <SectionDivider />

              <Section label="Greiðslumáti">
                <PaymentMethod {...paymentMethodProps} />
              </Section>
              <SectionDivider />
            </>
          )}
          <Section label="">
            <PaymentMethodInfo>
              {!person.isCustomer &&
                paymentMethodProps.method === 'invoice' &&
                `Greiðsluseðill mun birtast í netbankanum þínum við útgáfu trygginganna`}
            </PaymentMethodInfo>
            {!person.isCustomer && paymentMethodProps.method === 'card' && (
              <>
                <Heading>Kortaupplýsingar</Heading>
                <CreditCard {...creditCardProps} />
              </>
            )}
            <MobileButtonContainer>
              <Button onClick={completePurchase} loading={loading} disabled={loading}>
                GREIÐA
              </Button>
              {errorText && <ErrorText>{errorText}</ErrorText>}
            </MobileButtonContainer>
          </Section>
          <SectionDivider />
        </CheckOutInfo>
        <div>
          <CheckoutTable
            policies={quote.policies}
            canBuy={
              isEmail(contactInfoProps.email) || paymentMethodProps.method === 'card'
                ? !creditCardProps.paymentInputProps.meta.error || isEmail(contactInfoProps.email)
                : true
            }
            loading={loading}
            errorText={errorText}
            onBuy={completePurchase}
          />
          <LogOutButtonWrapper>
            <LogOutButton invisible />
          </LogOutButtonWrapper>
        </div>
      </CheckoutGrid>
    </Root>
  )
}

const ErrorText = styled.div`
  color: ${colors.errorRed};
  margin-top: 1rem;
  text-align: center;
`
export const Checkout = () => {
  const { state } = useAppState()
  if (!state.quote || !state.person) {
    return <Loading />
  }
  return <CheckoutComponent quote={state.quote} person={state.person} />
}

const MobileButtonContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: flex-start;

  margin-top: 3.5rem;

  ${breakpointUp('tablet')} {
    display: none;
  }
`

const PaymentMethodInfo = styled.p`
  font-size: 1.4rem;
  font-weight: 500;
`

const PageHeading = styled.h1`
  text-transform: uppercase;
  ${propBetween('font-size', 2.5, 3.75)}
  line-height: 120%;

  margin: 0;
  ${propBetween('margin-top', 2, 7.5)};
  ${propBetween('margin-bottom', 2, 7.5)};

  color: ${colors.black};

  ${breakpointDown('phone')} {
    margin: 2rem 0;
  }

  ${breakpointUp('desktopL')} {
    margin: 7.5rem 0;
  }
`

const Root = styled.div`
  padding: 0;

  ${propBetween('padding-left', 1.5, 6.5)};
  ${propBetween('padding-right', 1.5, 6.5)};

  margin: 2rem auto 0;

  max-width: 90rem;

  ${breakpointUp('tablet')} {
    margin-bottom: 8.5rem;
  }
`

const CheckoutGrid = styled.div`
  display: grid;
  ${propBetween('grid-column-gap', 2.5, 6.5, breakpoints.tablet / 16)}

  ${breakpointUp('tablet')} {
    grid-template-columns: 1fr 25rem;
    grid-template-areas: 'right left';
    & > div:last-child {
      grid-area: left;
      position: sticky;
      top: 1rem;
    }
  }

  ${breakpointUp('desktop')} {
    grid-template-columns: 1fr 30.625rem;
  }

  ${breakpointDown('tablet')} {
    grid-template-columns: 1fr;
  }
`

const CheckOutInfo = styled.div`
  ${breakpointDown('phone')} {
    order: 2;
  }
`
const LogOutButtonWrapper = styled.div`
  position: relative;
  margin-top: 15rem;
  margin-left: 3rem;
`
